import React from "react";
import { Grid, Paper, Box, Typography, Tooltip, IconButton } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  FingerprintRounded as FingerprintIcon,
} from "@mui/icons-material";
import { CameraCaptureDialog } from "../../../utils/imageCapture";
import SectionCard from "./SectionCard";
import ImageSourceDialog from "./ImageSourceDialog";
import FingerprintCaptureDialog from "./FingerprintCaptureDialog";
import { fingerprintSizeLabel } from "./fingerprint";
import {
  BG,
  BLUE,
  BLUE_DARK,
  BLUE_MED,
  BLUE_PALE,
  BLUE_SUBTLE,
  BORDER,
  BORDER_MED,
  ERROR_C,
  INK_MUTED,
  SUCCESS,
  SURFACE,
} from "./shared";

// ─── Documents & Verification section (step 4) ───────────────────────────────
// Presentational block extracted verbatim from PaymentScreen. All state and
// handlers continue to live in the parent and arrive here as props.
const DocumentsSection = ({
  stepStateOf,
  openSections,
  toggleSection,
  sectionSummary,
  paymentLocked,
  imageFields,
  images,
  setImageSourceDialog,
  handleRemoveImage,
  handleImageUpload,
  imageSourceDialog,
  setCameraField,
  cameraField,
  handleCameraCapture,
  fingerprintFields,
  fingerprints,
  fingerprintDialog,
  setFingerprintDialog,
  handleFingerprintCaptured,
  handleClearFingerprint,
}) => {
  return (
                <SectionCard
                  icon="📄"
                  title="Documents & Verification"
                  step={4}
                  stepState={stepStateOf(4)}
                  collapsed={!openSections[4]}
                  onToggle={() => toggleSection(4)}
                  summary={sectionSummary[4]}
                  readOnly={paymentLocked}
                >
                  <Grid container spacing={2}>
                    {imageFields.map((field) => (
                      <Grid item xs={12} sm={6} md={3} key={field.key}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: images[field.key]
                              ? `1px solid ${BLUE_MED}60`
                              : `1px solid ${BORDER}`,
                            bgcolor: images[field.key] ? BLUE_SUBTLE : SURFACE,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {images[field.key] ? (
                                <CheckCircleIcon
                                  sx={{ fontSize: 15, color: SUCCESS }}
                                />
                              ) : (
                                React.cloneElement(field.icon, {
                                  sx: { fontSize: 15, color: INK_MUTED },
                                })
                              )}
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                sx={{
                                  color: images[field.key]
                                    ? BLUE_DARK
                                    : INK_MUTED,
                                  fontSize: "0.72rem",
                                }}
                              >
                                {field.label}
                              </Typography>
                            </Box>
                            <Box>
                              {images[field.key] && (
                                <>
                                  <Tooltip title="Replace">
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        setImageSourceDialog({
                                          open: true,
                                          index: field.key,
                                        })
                                      }
                                      sx={{ color: BLUE, p: 0.3 }}
                                    >
                                      <RefreshIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove">
                                    <IconButton
                                      size="small"
                                      onClick={(e) =>
                                        handleRemoveImage(field.key, e)
                                      }
                                      sx={{ color: ERROR_C, p: 0.3 }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </Box>

                          <Box
                            onClick={() =>
                              setImageSourceDialog({
                                open: true,
                                index: field.key,
                              })
                            }
                            sx={{
                              position: "relative",
                              width: "100%",
                              height: 180,
                              borderRadius: "8px",
                              border: `2px dashed ${
                                images[field.key] ? BLUE_MED : BORDER_MED
                              }`,
                              bgcolor: images[field.key] ? BLUE_SUBTLE : BG,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              overflow: "hidden",
                              transition: "all 0.2s",
                              "&:hover": {
                                borderColor: BLUE,
                                bgcolor: BLUE_PALE,
                              },
                            }}
                          >
                            {images[field.key] ? (
                              <Box
                                component="img"
                                src={images[field.key]}
                                alt={field.label}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <>
                                <CloudUploadIcon
                                  sx={{
                                    fontSize: 26,
                                    color: BLUE_MED,
                                    opacity: 0.7,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: INK_MUTED,
                                    mt: 0.5,
                                    fontSize: "0.72rem",
                                  }}
                                >
                                  Tap to upload
                                </Typography>
                              </>
                            )}
                          </Box>

                          <input
                            id={`payment-camera-input-${field.key}`}
                            type="file"
                            hidden
                            accept="image/*"
                            capture="environment"
                            onChange={(e) =>
                              handleImageUpload(field.key, e.target.files?.[0])
                            }
                          />
                          <input
                            id={`payment-gallery-input-${field.key}`}
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={(e) =>
                              handleImageUpload(field.key, e.target.files?.[0])
                            }
                          />
                        </Paper>
                      </Grid>
                    ))}

                    {/* ── Fingerprints ────────────────────────────────────────
                        Same card shell as the photos, but the body is a scan
                        target instead of an image: the blob captured here goes
                        straight into pawn_broking.fingerprint_1 / _2, and a
                        finger left unscanned simply stays NULL. */}
                    {(fingerprintFields || []).map((field) => {
                      const fp = fingerprints?.[field.key] || null;
                      return (
                        <Grid item xs={12} sm={6} md={3} key={field.key}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: fp
                                ? `1px solid ${BLUE_MED}60`
                                : `1px solid ${BORDER}`,
                              bgcolor: fp ? BLUE_SUBTLE : SURFACE,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                {fp ? (
                                  <CheckCircleIcon
                                    sx={{ fontSize: 15, color: SUCCESS }}
                                  />
                                ) : (
                                  <FingerprintIcon
                                    sx={{ fontSize: 15, color: INK_MUTED }}
                                  />
                                )}
                                <Typography
                                  variant="caption"
                                  fontWeight={700}
                                  sx={{
                                    color: fp ? BLUE_DARK : INK_MUTED,
                                    fontSize: "0.72rem",
                                  }}
                                >
                                  {field.label}
                                </Typography>
                              </Box>
                              <Box>
                                {fp && (
                                  <>
                                    <Tooltip title="Re-scan">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setFingerprintDialog({
                                            open: true,
                                            field: field.key,
                                          })
                                        }
                                        sx={{ color: BLUE, p: 0.3 }}
                                      >
                                        <RefreshIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Remove">
                                      <IconButton
                                        size="small"
                                        onClick={(e) =>
                                          handleClearFingerprint(field.key, e)
                                        }
                                        sx={{ color: ERROR_C, p: 0.3 }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </Box>

                            <Box
                              onClick={() =>
                                setFingerprintDialog({
                                  open: true,
                                  field: field.key,
                                })
                              }
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: 180,
                                borderRadius: "8px",
                                border: `2px dashed ${
                                  fp ? BLUE_MED : BORDER_MED
                                }`,
                                bgcolor: fp ? BLUE_SUBTLE : BG,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                overflow: "hidden",
                                textAlign: "center",
                                px: 1,
                                transition: "all 0.2s",
                                "&:hover": {
                                  borderColor: BLUE,
                                  bgcolor: BLUE_PALE,
                                },
                              }}
                            >
                              <FingerprintIcon
                                sx={{
                                  fontSize: fp ? 40 : 30,
                                  color: fp ? SUCCESS : BLUE_MED,
                                  opacity: fp ? 1 : 0.7,
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: fp ? BLUE_DARK : INK_MUTED,
                                  fontWeight: fp ? 700 : 400,
                                  mt: 0.5,
                                  fontSize: "0.72rem",
                                }}
                              >
                                {fp ? "Captured" : "Tap to scan"}
                              </Typography>
                              {fp && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: INK_MUTED,
                                    fontSize: "0.66rem",
                                    mt: 0.25,
                                  }}
                                >
                                  {fp.source === "file" ? "From file" : "Scanned"}
                                  {fp.quality != null
                                    ? ` · quality ${fp.quality}`
                                    : ""}
                                  {fp.data
                                    ? ` · ${fingerprintSizeLabel(fp.data)}`
                                    : ""}
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        </Grid>
                      );
                    })}
                  </Grid>

                  <ImageSourceDialog
                    open={imageSourceDialog.open}
                    index={imageSourceDialog.index}
                    prefix="payment-"
                    onClose={() =>
                      setImageSourceDialog({ open: false, index: null })
                    }
                    onTakePhoto={() => setCameraField(imageSourceDialog.index)}
                  />
                  <CameraCaptureDialog
                    open={cameraField !== null}
                    onClose={() => setCameraField(null)}
                    onCapture={handleCameraCapture}
                  />
                  <FingerprintCaptureDialog
                    open={!!fingerprintDialog?.open}
                    field={fingerprintDialog?.field}
                    label={
                      (fingerprintFields || []).find(
                        (f) => f.key === fingerprintDialog?.field,
                      )?.label
                    }
                    onClose={() =>
                      setFingerprintDialog({ open: false, field: null })
                    }
                    onCaptured={handleFingerprintCaptured}
                  />
                </SectionCard>
  );
};

export default DocumentsSection;
